import { Grid, Table, TableBody, TableCell, TableRow, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CiCircleCheck } from 'react-icons/ci';
import { RiDeleteBin6Line } from 'react-icons/ri';
import {
  removeContactLinkInCandidateMaster,
  addingContactLinkInCandidateMaster,
  ICandidate,
} from 'wisestep-api';
import { WsButton, Label, WsIconText, WsColors, WsTypoColors } from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../../context';
import { success } from '../../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import { EditTextWithLabel } from '../UpdateCandidateDetailsCard';

import AddContactLink from './AddContactLink';
const ContactInfoCard = ({ candidate }: { candidate: ICandidate }) => {
  const methods = useFormContext();
  const workspaceContext = useWorkspaceContext();
  const [openAddLink, setOpenAddLink] = useState<boolean>(false);
  const [isEmail, setIsEmail] = useState<boolean>(false);
  const [isPhone, setIsPhone] = useState<boolean>(false);
  const [deleteEmail, setDeleteEmail] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const emails = [
    { value: true, label: methods.getValues('email') ?? '' },
    ...(methods.getValues('alternateEmails')?.map((email: string) => {
      return {
        value: false,
        label: email,
      };
    }) ?? []),
  ];
  const phone = [
    { value: true, label: candidate?.phoneNumber ?? '' },
    ...(candidate?.alternatePhoneNumbers?.map((phone: string) => {
      return {
        value: false,
        label: phone,
      };
    }) ?? []),
  ];
  const onAdd = (isEmailLink: boolean) => {
    if (isEmailLink && emails.length === 3) {
      return;
    } else if (!isEmailLink && phone.length === 3) {
      return;
    }
    setOpenAddLink(true);
    setIsEmail(isEmailLink);
    setIsPhone(!isEmailLink);
  };
  const refreshPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    const getRole = async () => {
      try {
        const role = workspaceContext?.role?.toLowerCase() || '';

        const onlyUserOrRecruiter = role.includes('user') || role.includes('recruiter');

        setDeleteEmail(onlyUserOrRecruiter);
      } catch (err) {
        console.error('Error fetching roles:', err);
      }
    };

    getRole();
  }, []);

  const deleteNewField = async (data: string | null, isPhoneNo: boolean) => {
    if (data) {
      await removeContactLinkInCandidateMaster({
        id: candidate?.candidateId ?? candidate?.id ?? '' ?? '',
        oldEmail: !isPhoneNo ? data : null,
        oldLink: null,
        oldPhoneNumber: isPhoneNo ? data : null,
        oldTag: null,
        updatePrimaryEmail: false,
      });

      setNotification(success('Field deleted'));
      refreshPage();
    }
  };

  const onMarkPrimary = async (data: string | null, isPhoneNo: boolean) => {
    if (data) {
      await addingContactLinkInCandidateMaster({
        id: candidate?.candidateId ?? candidate?.id ?? '' ?? '',
        newEmail: !isPhoneNo ? data : null,
        oldLink: null,
        newPhoneNumber: isPhoneNo ? data : null,
        oldTag: null,
        updatePrimaryEmail: !isPhoneNo,
        updatePrimaryPhone: isPhoneNo,
      });

      setNotification(success('Marked Primary '));
      refreshPage();
    }
  };

  return (
    <>
      <EditTextWithLabel name="name" labelText="Name" required={false} />
      <Grid item xs={12}>
        <ContactData
          data={emails}
          label="Choose Primary Email"
          onDelete={(link) => deleteNewField(link, false)}
          onMarkPrimary={(link) => onMarkPrimary(link, false)}
          disableDelete={deleteEmail}
        />
      </Grid>
      <Grid item xs={8}>
        <WsButton
          type="button"
          variant="outlined"
          onClick={() => onAdd(true)}
          disabled={emails.length >= 3}
        >
          Add Email Address
        </WsButton>
      </Grid>
      <Grid item xs={12}>
        <ContactData
          data={phone}
          label="Choose Primary Phone No."
          onDelete={(link) => deleteNewField(link, true)}
          onMarkPrimary={(link) => onMarkPrimary(link, true)}
        />
      </Grid>
      <Grid item xs={8}>
        <WsButton
          type="button"
          variant="outlined"
          onClick={() => onAdd(false)}
          disabled={phone.length >= 3}
        >
          Add Phone No.
        </WsButton>
      </Grid>
      <Grid item xs={12}>
        <AddContactLink
          open={openAddLink}
          onClose={() => {
            setOpenAddLink(false);
            setIsEmail(false);
            setIsPhone(false);
          }}
          isEmail={isEmail}
          isPhone={isPhone}
          id={candidate?.candidateId ?? candidate?.id ?? ''}
          candidate={candidate}
        />
      </Grid>
    </>
  );
};

export default ContactInfoCard;

const ContactData = ({
  data,
  label,
  onDelete,
  onMarkPrimary,
  disableDelete = false,
}: {
  data: {
    value: boolean;
    label: string;
  }[];
  label?: string;
  onDelete: (link: string) => void;
  onMarkPrimary: (link: string) => void;
  disableDelete?: boolean;
}) => (
  <>
    <Label>{label}</Label>
    <Table sx={{ border: '1px solid' }}>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row.label}>
            <TableCell component="th" scope="row">
              {row.label}
            </TableCell>
            <TableCell align="right">
              <WsIconText
                text={row.value ? 'Primary Address' : 'Mark Primary'}
                icon={row.value ? <CiCircleCheck size={24} color={WsColors.ForestGreen} /> : <></>}
                onClick={() => onMarkPrimary(row.label)}
                disable={row.value}
                textCursor={'pointer'}
                textColor={row.value ? WsTypoColors.FORESTGREEN : WsTypoColors.PRIMARY}
              />
              {!row.value && (
                <IconButton
                  aria-label="show more"
                  onClick={() => !disableDelete && onDelete(row.label)}
                  sx={{ color: !disableDelete ? WsColors.Red400 : WsColors.Gray88 }}
                >
                  <RiDeleteBin6Line />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </>
);
